import { i18n } from '@fs/zion-locale'
import {
  conferenceConfig as conferenceExperienceConfig,
  getLiveWindowEnd,
  parseVideoDuration,
} from '@fs/zion-rootstech'
import { serverConfig, hour, minute, second, watchHistoryConfig, day } from '../../config'
import { getDuration } from './videoHelpers'

export const getNowTimestamp = () => {
  return Date.now() + serverConfig.timestampOffset
}

export const getI18nDate = (dateTime, showOpts = {}) => {
  const { weekday, time, timezone, year, long, timeZone } = showOpts
  const date = new Date(dateTime)
  const abrevLength = long ? 'long' : 'short'
  const options = {
    weekday: weekday ? abrevLength : undefined,
    day: weekday ? undefined : 'numeric',
    month: weekday ? undefined : abrevLength,
    year: !weekday && year ? 'numeric' : undefined,
    hour: time ? 'numeric' : undefined,
    minute: time ? '2-digit' : undefined,
    timeZoneName: timezone ? 'short' : undefined,
    timeZone,
  }
  const formattedDate = new Intl.DateTimeFormat(i18n.language, options).format(date)
  return formattedDate.replace(/\s/, ' ')
}

export const roundTimestampDown = ({ to = 'minute', time }) => {
  const timeMap = { hour, minute, second }
  const divider = timeMap[to]
  return Math.floor(time / divider) * divider
}

export const parseISODuration = (timestamp) => {
  if (!timestamp) return 0
  const durationRegex = /(-)?PT(?:([.,\d]+)H)?(?:([.,\d]+)M)?(?:([.,\d]+)S)?/

  const matches = durationRegex.exec(timestamp)
  if (!matches) {
    throw new Error('Invalid ISO 8601 duration format')
  }

  const [seconds = 0, minutes = 0, hours = 0] = matches.reverse()

  const totalMilliseconds = parseInt(hours, 10) * hour + parseInt(minutes, 10) * minute + parseInt(seconds, 10) * second

  return totalMilliseconds
}

const parseMsDuration = (ms) => {
  const dt = new Date(ms)
  const units = [
    ['Y', dt.getUTCFullYear() - 1970],
    ['M', dt.getUTCMonth()],
    ['D', dt.getUTCDate() - 1],
    ['T', null], // separates date and time in ISO 8601
    ['H', dt.getUTCHours()],
    ['M', dt.getUTCMinutes()],
    ['S', dt.getUTCSeconds()],
  ]

  let duration = units.reduce((acc, [k, v]) => {
    if (v) {
      acc += v + k
    } else if (k === 'T') {
      acc += k
    }
    return acc
  }, '')

  duration = duration.endsWith('T') ? duration.slice(0, -1) : duration
  return duration ? `P${duration}` : null
}

/**
 * Converts an ISO 8601 timestamp to a specified interval value.
 *
 * @param {string} timestamp - The ISO 8601 formatted timestamp.
 * @returns {number} The converted timestamp into ms.
 */
export const parseISOtoMs = (isoString) => {
  if (isoString.includes('P')) {
    return parseISODuration(isoString)
  }
  return Date.parse(isoString)
}

export const getPercentageWatched = (item) => {
  if (item?.watchHistory?.completed) {
    return 100
  }
  const mostRecentPosition = parseISODuration(item?.watchHistory?.mostRecentPosition) / second
  const durationStr = getDuration(item)
  // either live or no duration in BS
  if (item?.isLive) {
    const time = getNowTimestamp() - item.date
    const progressBar = time / (getLiveWindowEnd({ date: item.date, videoDuration: durationStr }) - item.date)

    return progressBar * 100
  }
  const durationMs = parseVideoDuration(durationStr)
  if (!durationMs) {
    return mostRecentPosition ? watchHistoryConfig.NO_DURATION_PERCENT_COMPLETED : 0
  }

  const timeWatchedDecimal = mostRecentPosition / (durationMs / 1000)
  return timeWatchedDecimal * 100
}

export const parseMsToISO = (ms, shouldReturnWithLocalOffset) => {
  // it should be a duration;
  // 28 days is just a guess, I don't think we'll need a duration longer than that
  if (ms < 28 * day) {
    return parseMsDuration(ms)
  }
  // it should be a date
  let date = new Date(ms)
  if (shouldReturnWithLocalOffset) {
    date = new Date(date.getTime() + date.getTimezoneOffset() * minute)
  }

  return date.toISOString()
}

export const convertMsToTime = (ms) => {
  const seconds = Math.floor(ms / second)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  return {
    days: Math.max(days, 0),
    hours: Math.max(hours % 24, 0),
    minutes: Math.max(minutes % 60, 0),
    seconds: Math.max(seconds % 60, 0),
  }
}

export const dateTimeFormat = (dates, options) => {
  if (Array.isArray(dates)) {
    return new Intl.DateTimeFormat(i18n.language, options).formatRange(new Date(dates[0]), new Date(dates[1]))
  }
  return new Intl.DateTimeFormat(i18n.language, options).format(new Date(dates))
}

const padDuration = (value) => (value < 10 ? `0${value}` : value)

export const formatDuration = (duration) => {
  if (!duration) {
    return duration
  }

  let ms = 0
  if (duration instanceof String || typeof duration === 'string') {
    if (duration.indexOf(':') !== -1) {
      return duration
    }

    if (duration.startsWith('PT')) {
      ms = parseISODuration(duration)
    }
  } else if (Number.isInteger(duration)) {
    ms = duration
  }

  const time = convertMsToTime(ms)

  let result = ''
  if (time.days) {
    result += `${time.days}:`
  }

  result += time.hours ? padDuration(time.hours) : '00'
  result += ':'

  result += time.minutes ? padDuration(time.minutes) : '00'
  result += ':'

  result += time.seconds ? padDuration(time.seconds) : '00'
  return result
}

export const isCurrentlyAvailable = ({ now, timestamp, year, currentConferenceYear }) => {
  // for sessions without a timestamp, they are available as soon as the conference goes live.
  // this handles sessions that were part of the conference that just ended but don't have dates on them
  if (year && Number(year) === Number(currentConferenceYear) && now > conferenceExperienceConfig.startTimestamp) {
    return true
  }

  if (timestamp) {
    return now > timestamp
  }

  if (year && currentConferenceYear > Number(year)) {
    return true
  }

  return false
}
